import React, { useCallback, useEffect, useRef, useState } from 'react';
import { readConversationLocation, rememberConversationLocation } from '../lib/workspaceLocation';
import { useTheme } from '../theme/ThemeProvider';
import { useSession } from '../context/SessionContext';
import { ConversationSidebar } from '../components/conversation/ConversationSidebar';
import { IconSidebar } from '../components/conversation/icons';
import { ConversationHeader } from '../components/conversation/ConversationHeader';
import { OziraResponseActions, type ResponseFeedback } from '../components/conversation/OziraResponseActions';
import { MessageMenu } from '../components/conversation/MessageMenu';
import { WeatherCard } from '../components/conversation/WeatherCard';
import { WebsitePreviewCard } from '../components/conversation/WebsitePreviewCard';
import { PlaceCard } from '../components/conversation/PlaceCard';
import { PlaceListCard } from '../components/conversation/PlaceListCard';
import { DirectionsCard } from '../components/conversation/DirectionsCard';
import { Composer } from '../components/conversation/Composer';
import { ToolsPicker } from '../components/conversation/ToolsPicker';
import { ConnectorChooser } from '../components/conversation/ConnectorChooser';
import { ShareDialog } from '../components/conversation/ShareDialog';
import { ImageViewer } from '../components/conversation/ImageViewer';
import { LiveVoicePanel } from '../components/conversation/LiveVoicePanel';
import { HomeScreen } from './HomeScreen';
import { ActivityScreen } from './ActivityScreen';
import { FilesScreen } from './FilesScreen';
import { conversationWorkspaceStrings } from '../i18n/conversationWorkspace';
import { assistantSendErrorMessage } from '../lib/assistantErrors';
import '../components/conversation/conversationWorkspace.css';
import {
  listConversations,
  listProjects,
  createProject,
  getConversation,
  saveConversation,
  deleteConversation,
  setConversationMeta,
  sendChat,
  sendVisionMessage,
  sendResearch,
  speak,
  submitFeedback,
  saveBookmark,
  unsaveBookmark,
  listBookmarkedIndexes,
  type ConversationSummary,
  type ConversationMessage,
  type ProjectSummary,
  type WeatherCardData,
  type PlaceCardData,
  type DirectionsCardData,
  type WebBuildCardData,
} from '../lib/conversationsApi';

export interface AssistantSource {
  label: string;
  href?: string;
  verified?: boolean;
}

export interface AssistantToolMetadata {
  name: string;
  status: string;
  detail?: string;
}

export interface AssistantResponseMetadata {
  sources?: AssistantSource[];
  verified?: boolean;
  tool?: AssistantToolMetadata;
  weather?: WeatherCardData;
  place?: PlaceCardData;
  places?: PlaceCardData[];
  directions?: DirectionsCardData;
  webBuild?: WebBuildCardData;
}

interface AssistantImage {
  id: string;
  url: string;
  mediaType?: string;
}

// Display-only scaling of the already-generated picture -- no new pixels,
// no second API call. Real generation size (square/portrait/landscape) is a
// separate control at request time, wired to lib/providers.js's IMAGE_SIZES
// via sendChat's size param; this is the "resize the picture after it's
// made" half of that feature.
const GENERATED_IMAGE_DISPLAY_WIDTHS = { small: 320, medium: 480, large: 640 } as const;
type GeneratedImageDisplaySize = keyof typeof GENERATED_IMAGE_DISPLAY_WIDTHS;

function GeneratedImageCard({ image, onOpen }: { image: AssistantImage; onOpen: (url: string) => void }) {
  const [displaySize, setDisplaySize] = useState<GeneratedImageDisplaySize>('medium');
  // Same-origin static file (server.js's GET /img/<file>) -- a real download
  // of the actual generated image, not a placeholder link.
  const fileName = image.url.split('/').pop() || 'ozira-image.png';
  return (
    <div className="ozira-cw-generated-image-wrap">
      <button
        type="button"
        className="ozira-cw-generated-image-open"
        aria-label="Open full size"
        title="Open full size"
        onClick={() => onOpen(image.url)}
      >
        <img
          className="ozira-cw-generated-image"
          src={image.url}
          alt="Generated by OZIRA"
          style={{ maxWidth: GENERATED_IMAGE_DISPLAY_WIDTHS[displaySize] }}
        />
      </button>
      <div className="ozira-cw-generated-image-actions">
        <a className="ozira-cw-generated-image-action" href={image.url} download={fileName}>
          Save
        </a>
        <div className="ozira-cw-generated-image-resize" role="group" aria-label="Resize image">
          {(Object.keys(GENERATED_IMAGE_DISPLAY_WIDTHS) as GeneratedImageDisplaySize[]).map((size) => (
            <button
              key={size}
              type="button"
              className={
                displaySize === size
                  ? 'ozira-cw-generated-image-resize__option ozira-cw-generated-image-resize__option--active'
                  : 'ozira-cw-generated-image-resize__option'
              }
              aria-pressed={displaySize === size}
              aria-label={size === 'small' ? 'Small' : size === 'medium' ? 'Medium' : 'Large'}
              onClick={() => setDisplaySize(size)}
            >
              {size === 'small' ? 'S' : size === 'medium' ? 'M' : 'L'}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function renderInline(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const token = /(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^\s)]+\))/g;
  let cursor = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = token.exec(text))) {
    if (match.index > cursor) nodes.push(text.slice(cursor, match.index));
    const value = match[0];
    if (value.startsWith('`')) nodes.push(<code key={key++}>{value.slice(1, -1)}</code>);
    else if (value.startsWith('**')) nodes.push(<strong key={key++}>{value.slice(2, -2)}</strong>);
    else {
      const link = value.match(/^\[([^\]]+)\]\(([^\s)]+)\)$/);
      if (link) nodes.push(<a key={key++} href={link[2]} target="_blank" rel="noreferrer">{link[1]}</a>);
    }
    cursor = match.index + value.length;
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

export function renderAssistantContent(content: string, metadata?: AssistantResponseMetadata, image?: AssistantImage, onOpenImage?: (url: string) => void) {
  const lines = content.split(/\r?\n/);
  const blocks: React.ReactNode[] = [];
  let index = 0;
  let key = 0;
  while (index < lines.length) {
    const raw = lines[index].trim();
    if (!raw) { index += 1; continue; }
    if (raw.startsWith('```')) {
      const language = raw.slice(3).trim();
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].trim().startsWith('```')) code.push(lines[index++]);
      if (index < lines.length) index += 1;
      blocks.push(<pre key={key++} data-language={language || undefined}><code>{code.join('\n')}</code></pre>);
      continue;
    }
    const heading = raw.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      blocks.push(<h3 key={key++}>{renderInline(heading[2])}</h3>);
      index += 1;
      continue;
    }
    const listMatch = raw.match(/^([-*])\s+(.+)$/) || raw.match(/^\d+[.)]\s+(.+)$/);
    if (listMatch) {
      const ordered = /^\d/.test(raw);
      const items: React.ReactNode[] = [];
      while (index < lines.length) {
        const current = lines[index].trim();
        const item = ordered ? current.match(/^\d+[.)]\s+(.+)$/) : current.match(/^[-*]\s+(.+)$/);
        if (!item) break;
        items.push(<li key={items.length}>{renderInline(item[1])}</li>);
        index += 1;
      }
      blocks.push(ordered ? <ol key={key++}>{items}</ol> : <ul key={key++}>{items}</ul>);
      continue;
    }
    if (raw.includes('|') && index + 1 < lines.length && /^\s*\|?\s*:?-{3,}/.test(lines[index + 1])) {
      const cells = (line: string) => line.replace(/^\||\|$/g, '').split('|').map((cell) => cell.trim());
      const headers = cells(raw);
      index += 2;
      const rows: React.ReactNode[] = [];
      while (index < lines.length && lines[index].includes('|')) {
        rows.push(<tr key={rows.length}>{cells(lines[index++]).map((cell, cellIndex) => <td key={cellIndex}>{renderInline(cell)}</td>)}</tr>);
      }
      blocks.push(<table key={key++}><thead><tr>{headers.map((cell) => <th key={cell}>{renderInline(cell)}</th>)}</tr></thead><tbody>{rows}</tbody></table>);
      continue;
    }
    const paragraph: string[] = [raw];
    index += 1;
    while (index < lines.length && lines[index].trim() && !/^(#{1,3})\s|^```|^([-*])\s|^\d+[.)]\s/.test(lines[index].trim())) paragraph.push(lines[index++].trim());
    blocks.push(<p key={key++}>{renderInline(paragraph.join(' '))}</p>);
  }
  const outputKind = metadata?.weather
    ? 'Live weather'
    : metadata?.place
      ? 'Place details'
      : metadata?.places?.length
        ? 'Nearby places'
        : metadata?.directions
          ? 'Directions'
          : metadata?.tool
            ? metadata.tool.name
            : metadata?.sources?.length
              ? 'Sourced answer'
              : 'Answer';
  return (
    <article className="ozira-cw-answer-card" aria-label="OZIRA answer">
      <header className="ozira-cw-answer-card__header">
        <span className="ozira-cw-answer-card__brand">OZIRA</span>
        <span className="ozira-cw-answer-card__kind">{outputKind}</span>
        {metadata?.verified ? <span className="ozira-cw-answer-card__verified" role="status">Verified</span> : null}
      </header>
      <div className="ozira-cw-response-content">
        {metadata?.weather ? <WeatherCard weather={metadata.weather} sourceLabel={metadata.sources?.[0]?.label || 'Google Search'} /> : null}
        {metadata?.place ? <PlaceCard place={metadata.place} /> : null}
        {metadata?.places?.length ? <PlaceListCard places={metadata.places} /> : null}
        {metadata?.directions ? <DirectionsCard directions={metadata.directions} /> : null}
        {metadata?.webBuild ? <WebsitePreviewCard webBuild={metadata.webBuild} /> : null}
        {blocks}
        {image ? <GeneratedImageCard image={image} onOpen={onOpenImage ?? (() => {})} /> : null}
        {/* One consolidated, click-to-expand place for every source on this
            response, instead of an always-visible chip list -- expanding it
            answers "where did this come from" via the real tool detail
            (lib/liveData.js's groundingMetadata) plus each source link. */}
        {metadata?.sources?.length ? (
          <details className="ozira-cw-provenance">
            <summary className="ozira-cw-provenance-summary">
              <span>Sources</span>
              <span className="ozira-cw-provenance-summary-meta">{metadata.sources.length} source{metadata.sources.length === 1 ? '' : 's'}</span>
            </summary>
            <div className="ozira-cw-provenance-panel">
              {metadata.tool ? (
                <div className="ozira-cw-provenance-overview">
                  <span className="ozira-cw-provenance-count">
                    {`From ${metadata.tool.name}${metadata.tool.detail ? ` · ${metadata.tool.detail}` : ''}`}
                  </span>
                </div>
              ) : null}
              <ul className="ozira-cw-provenance-sources">
                {metadata.sources.map((source) => (
                  <li className="ozira-cw-provenance-source" key={`${source.label}-${source.href ?? ''}`}>
                    {source.href ? <a href={source.href} target="_blank" rel="noreferrer">{source.label}</a> : <span>{source.label}</span>}
                    {source.verified ? <span className="ozira-cw-provenance-verified"> · Verified</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          </details>
        ) : metadata?.tool ? (
          <div className="ozira-cw-tool-card"><span className="ozira-cw-badge">Tool</span><strong>{metadata.tool.name}</strong><span>{metadata.tool.status}</span>{metadata.tool.detail ? <small>{metadata.tool.detail}</small> : null}</div>
        ) : null}
      </div>
    </article>
  );
}

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'ozira-sidebar-collapsed';

// Real values from lib/providers.js's IMAGE_SIZES -- the request-time
// generation size, distinct from GeneratedImageCard's post-hoc display
// resize above.
const IMAGE_GENERATION_SIZES: Record<'square' | 'portrait' | 'landscape', string> = {
  square: '1024x1024',
  portrait: '1024x1536',
  landscape: '1536x1024',
};

// Desktop landing default: a collapsed icon rail, not the fully expanded
// history-heavy sidebar -- ChatGPT's own pattern, requested explicitly. Reads
// a per-browser preference so a user who chose to keep it open stays that
// way; defaults to collapsed (true) the first time. Guarded for jsdom/private
// browsing, where localStorage can throw or simply not exist.
function readSidebarCollapsedPref(): boolean {
  try {
    if (typeof window === 'undefined') return true;
    const stored = window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY);
    return stored === null ? true : stored === '1';
  } catch {
    return true;
  }
}

function writeSidebarCollapsedPref(collapsed: boolean) {
  try {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    // Private browsing / storage disabled -- the toggle still works for this
    // page view, it just won't be remembered on the next visit.
  }
}

// Must match conversationWorkspace.css's `@media (min-width: 768px)` guard on
// the collapsed rail's own styling (.ozira-cw-rail-logo, .ozira-cw-rail-icon,
// .ozira-cw-sidebar.is-collapsed's 56px width, etc). Reported live: on a real
// phone, .ozira-assistant-mobile-topbar's hamburger hadn't been tapped yet
// (mobileSidebarOpen still false) while the collapsed pref defaulted to true,
// so the rail rendered -- with none of its desktop-only CSS, since the phone
// is under 768px. mobileSidebarOpen alone can't stand in for "is this a
// desktop viewport": it only tracks whether the mobile drawer is *open*, not
// how wide the screen is. Tracking the same breakpoint here closes that gap.
const DESKTOP_RAIL_MEDIA_QUERY = '(min-width: 768px)';

function useIsDesktopViewport(): boolean {
  const [isDesktop, setIsDesktop] = useState(() => {
    try {
      return typeof window !== 'undefined' && window.matchMedia(DESKTOP_RAIL_MEDIA_QUERY).matches;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia(DESKTOP_RAIL_MEDIA_QUERY);
    } catch {
      return;
    }
    const onChange = () => setIsDesktop(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isDesktop;
}

// Conversation work surface for the approved Assistant frame (Figma 66:171).
export function AssistantScreen({
  mobileSidebarOpen = false,
  onMobileSidebarClose,
  onMobileSidebarCloseWithoutRestore,
  onMobileSidebarOpen,
  onOpenSettings,
  activeSection = 'assistant',
  onNavigate,
  onNavigateProduct,
}: {
  mobileSidebarOpen?: boolean;
  onMobileSidebarClose?: () => void;
  onMobileSidebarCloseWithoutRestore?: () => void;
  onMobileSidebarOpen?: () => void;
  onOpenSettings?: () => void;
  activeSection?: 'home' | 'assistant' | 'activity' | 'files';
  onNavigate?: (section: 'home' | 'assistant' | 'activity' | 'files') => void;
  onNavigateProduct?: (productId: string) => void;
} = {}) {
  const { language } = useTheme();
  const { organization } = useSession();
  const t = (key: string) => conversationWorkspaceStrings[language]?.[key] ?? conversationWorkspaceStrings.en[key] ?? key;

  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => readSidebarCollapsedPref());
  const isDesktopViewport = useIsDesktopViewport();

  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const selectionRevision = useRef(0);
  const [restoringConversation, setRestoringConversation] = useState(() => Boolean(readConversationLocation()));
  const [conversationLoadFailed, setConversationLoadFailed] = useState(false);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [feedbackByIndex, setFeedbackByIndex] = useState<Record<number, ResponseFeedback>>({});
  const [savedIndexes, setSavedIndexes] = useState<Set<number>>(new Set());
  const [sending, setSending] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [connectorsOpen, setConnectorsOpen] = useState(false);
  const [liveVoiceOpen, setLiveVoiceOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  // Size to request when selectedTool === 'createImage' -- real values from
  // lib/providers.js's IMAGE_SIZES, not a display-only label.
  const [imageSizeOption, setImageSizeOption] = useState<'square' | 'portrait' | 'landscape'>('square');
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sendErrorMessage, setSendErrorMessage] = useState<string | null>(null);
  const [failedSend, setFailedSend] = useState(false);
  const [assistantOptionsOpen, setAssistantOptionsOpen] = useState(false);
  const [shareMessageIndex, setShareMessageIndex] = useState<number | null>(null);
  const [modelId, setModelId] = useState('auto');
  const [effortId, setEffortId] = useState('balanced');
  const [conversationShareOpen, setConversationShareOpen] = useState(false);
  const [sidebarShareId, setSidebarShareId] = useState<string | null>(null);
  const [pendingImage, setPendingImage] = useState<{ file: File; base64: string; mimeType: string } | null>(null);
  const [viewerImageUrl, setViewerImageUrl] = useState<string | null>(null);
  const [pendingImageEdit, setPendingImageEdit] = useState<{ sourceUrl: string; operation?: string; size?: string } | null>(null);
  const [composerPrefill, setComposerPrefill] = useState<{ text: string; nonce: number } | null>(null);
  const [dictationTrigger, setDictationTrigger] = useState<{ nonce: number } | null>(null);

  const refreshConversations = useCallback(async (reportError = true) => {
    try {
      const list = await listConversations(
        searchQuery ? { q: searchQuery } : { project: activeProjectId ?? undefined, folder: showArchived ? '__archived' : undefined },
      );
      setConversations(list);
    } catch {
      if (reportError) setLoadError('load');
    }
  }, [searchQuery, activeProjectId, showArchived]);

  useEffect(() => {
    // Conversation history is auxiliary to a new chat. A transient history
    // failure must not replace the main composer state or imply chat is down.
    void refreshConversations(false);
  }, [refreshConversations]);

  useEffect(() => {
    listProjects().then(setProjects).catch(() => {});
  }, []);

  const activeConversation = conversations.find((c) => c.id === activeConversationId) ?? null;

  const handleToggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      writeSidebarCollapsedPref(next);
      return next;
    });
  };

  // The desktop collapse preference never applies inside the mobile overlay
  // -- a 56px icon rail sliding in as the mobile drawer would be a bug, not
  // a feature -- nor on a narrow viewport before the mobile drawer has even
  // been opened, when the collapsed rail's own CSS (gated to the same
  // min-width:768px breakpoint via isDesktopViewport) wouldn't apply yet.
  const railCollapsed = sidebarCollapsed && !mobileSidebarOpen && isDesktopViewport;

  const handleNewChat = useCallback(() => {
    ++selectionRevision.current;
    rememberConversationLocation(null);
    setRestoringConversation(false);
    setConversationLoadFailed(false);
    setLoadError(null);
    setActiveConversationId(null);
    setMessages([]);
    setFeedbackByIndex({});
    setSavedIndexes(new Set());
  }, []);

  const handleSelect = useCallback(async (id: string) => {
    const revision = ++selectionRevision.current;
    rememberConversationLocation(id);
    setRestoringConversation(true);
    setConversationLoadFailed(false);
    setLoadError(null);
    setMessages([]);
    setActiveConversationId(id);
    setFeedbackByIndex({});
    setSavedIndexes(new Set());
    try {
      const detail = await getConversation(id);
      if (revision !== selectionRevision.current) return;
      setMessages(detail.messages || []);
      const restored: Record<number, ResponseFeedback> = {};
      (detail.messages || []).forEach((m, i) => {
        if (m.reaction === 1) restored[i] = 'helpful';
        else if (m.reaction === -1) restored[i] = 'notHelpful';
      });
      setFeedbackByIndex(restored);
      listBookmarkedIndexes(id).then((indexes) => {
        if (revision === selectionRevision.current) setSavedIndexes(new Set(indexes));
      }).catch(() => {});
    } catch {
      if (revision === selectionRevision.current) {
        setLoadError('load');
        setConversationLoadFailed(true);
      }
    } finally {
      if (revision === selectionRevision.current) setRestoringConversation(false);
    }
  }, []);

  useEffect(() => {
    const restore = () => {
      const id = readConversationLocation();
      if (id) void handleSelect(id);
      else handleNewChat();
    };
    restore();
    window.addEventListener('popstate', restore);
    return () => {
      ++selectionRevision.current;
      window.removeEventListener('popstate', restore);
    };
  }, [handleSelect, handleNewChat]);

  const handleToggleSave = async (index: number) => {
    if (!activeConversationId) return;
    const alreadySaved = savedIndexes.has(index);
    // Optimistic toggle, matching the Helpful/NotHelpful pattern.
    setSavedIndexes((prev) => {
      const next = new Set(prev);
      if (alreadySaved) next.delete(index);
      else next.add(index);
      return next;
    });
    try {
      if (alreadySaved) await unsaveBookmark(activeConversationId, index);
      else await saveBookmark(activeConversationId, index);
    } catch {
      // Roll back on failure -- never claim a saved state the server didn't confirm.
      setSavedIndexes((prev) => {
        const next = new Set(prev);
        if (alreadySaved) next.add(index);
        else next.delete(index);
        return next;
      });
    }
  };

  const persist = async (nextMessages: ConversationMessage[]) => {
    const result = await saveConversation({
      id: activeConversationId ?? undefined,
      projectId: activeProjectId,
      messages: nextMessages,
    });
    if (result.id !== activeConversationId) {
      setActiveConversationId(result.id);
      rememberConversationLocation(result.id);
    }
    void refreshConversations(false);
  };

  const readReplyAloud = (text: string) => {
    void speak({ text, lang: language }).then((result) => {
      if (result.audio) new Audio(result.audio).play().catch(() => {});
    }).catch(() => {});
  };

  const handleSend = async (text: string, options?: { viaVoice?: boolean }) => {
    const nextMessages: ConversationMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setSending(true);
    setLoadError(null);
    setSendErrorMessage(null);
    setFailedSend(false);
    const image = pendingImage;
    setPendingImage(null);
    const imageEdit = pendingImageEdit;
    setPendingImageEdit(null);
    try {
      let reply: string;
      let replyImage: string | undefined;
      let replyModel: string | undefined;
      let replyMetadata: ConversationMessage['metadata'];
      let replyCredits: number | undefined;
      if (image) {
        // A message sent with an attached image is answered by the real
        // vision model instead of plain chat -- /api/chat has no image input
        // of its own, so this is the only backend that can actually look at
        // the picture the user just uploaded.
        const result = await sendVisionMessage({
          messages: nextMessages.map((m) => ({ role: m.role, content: m.content })),
          images: [{ base64: image.base64, mimeType: image.mimeType }],
          lang: language,
          effort: effortId,
        });
        reply = result.reply;
        replyCredits = result.creditCost;
      } else if (selectedTool === 'webSearch' || selectedTool === 'deepResearch') {
        // Picking either of these from the "+" menu used to set toolMode,
        // which server.js's /api/chat handler never reads -- the request
        // was indistinguishable from plain chat. POST /api/ai/research is
        // the real backend both should call (lib/ai-ext.js's research()).
        const toolName = selectedTool === 'deepResearch' ? 'Deep research' : 'Web search';
        const result = await sendResearch(text);
        reply = result.configured ? (result.text || result.error || 'No result.') : (result.notice || 'Research is not set up yet.');
        replyModel = toolName;
        replyMetadata = {
          tool: { name: toolName, status: result.sources?.length ? `${result.sources.length} source${result.sources.length === 1 ? '' : 's'}` : 'Complete' },
          sources: (result.sources || []).map((source) => ({ label: source.title || source.url, href: source.url })),
        };
      } else {
        const result = await sendChat({
          messages: nextMessages.map((m) => ({ role: m.role, content: m.content, metadata: m.metadata })),
          projectId: activeProjectId,
          lang: language,
          toolMode: selectedTool ?? undefined,
          model: modelId,
          effort: effortId,
          // Explicit tool selection always wins over guessing from wording --
          // server.js's isImageGenerationRequest() already short-circuits to
          // true for skill: 'image', which toolMode alone never reached.
          // An image-viewer edit (reframe/redesign/remove background) is the
          // same skill: 'image' path with a sourceUrl -- server.js forwards
          // sourceUrl/operation into the same processImage() call either way.
          ...(selectedTool === 'createImage' || imageEdit
            ? {
                skill: 'image',
                size: imageEdit?.size ?? IMAGE_GENERATION_SIZES[imageSizeOption],
                ...(imageEdit?.sourceUrl ? { sourceUrl: imageEdit.sourceUrl } : {}),
                ...(imageEdit?.operation ? { operation: imageEdit.operation } : {}),
              }
            : {}),
        });
        reply = result.reply;
        replyImage = result.images?.[0]?.url;
        replyModel = result.model;
        replyMetadata = result.metadata;
        replyCredits = result.credits;
      }
      const withReply: ConversationMessage[] = [...nextMessages, { role: 'assistant', content: reply, image: replyImage, model: replyModel, metadata: replyMetadata, credits: replyCredits }];
      setMessages(withReply);
      await persist(withReply);
      // A message that started as spoken input gets its reply read back
      // automatically -- the same real /api/ai/speak text-to-speech the
      // manual "Read Aloud" message-menu action already uses.
      if (options?.viaVoice && reply) readReplyAloud(reply);
    } catch (err) {
      console.error('[AssistantScreen] handleSend failed:', err);
      setLoadError('send');
      setSendErrorMessage(assistantSendErrorMessage(err, t('sendFailed')));
      setFailedSend(true);
    } finally {
      setSending(false);
      setSelectedTool(null);
    }
  };

  const handleRetrySend = async () => {
    if (sending || !failedSend || messages.length === 0 || messages[messages.length - 1]?.role !== 'user') return;
    const requestMessages = messages;
    setSending(true);
    setLoadError(null);
    setSendErrorMessage(null);
    try {
      const result = await sendChat({
        messages: requestMessages.map((m) => ({ role: m.role, content: m.content, metadata: m.metadata })),
        projectId: activeProjectId,
        lang: language,
        toolMode: selectedTool ?? undefined,
        model: modelId,
        effort: effortId,
      });
      const withReply: ConversationMessage[] = [...requestMessages, { role: 'assistant', content: result.reply, image: result.images?.[0]?.url, model: result.model, metadata: result.metadata, credits: result.credits }];
      setMessages(withReply);
      await persist(withReply);
      setFailedSend(false);
    } catch (err) {
      console.error('[AssistantScreen] handleRetrySend failed:', err);
      setLoadError('send');
      setSendErrorMessage(assistantSendErrorMessage(err, t('sendFailed')));
    } finally {
      setSending(false);
      setSelectedTool(null);
    }
  };

  // Retry regenerates the LAST assistant response only. It re-sends the
  // conversation history truncated to just before that reply and replaces
  // it in place -- it never re-runs the user's original message as a new
  // consequential action and never silently repeats anything beyond the
  // model call itself (spec: "Retry means regenerate response ONLY").
  const handleRetryLast = async (index: number) => {
    if (sending) return;
    if (messages[index]?.role !== 'assistant') return;
    const priorTurn = messages.slice(0, index);
    setSending(true);
    setLoadError(null);
    setSendErrorMessage(null);
    try {
      const result = await sendChat({
        messages: priorTurn.map((m) => ({ role: m.role, content: m.content, metadata: m.metadata })),
        projectId: activeProjectId,
        lang: language,
        model: modelId,
        effort: effortId,
      });
      const updated = [...messages];
      updated[index] = { role: 'assistant', content: result.reply, image: result.images?.[0]?.url, model: result.model, metadata: result.metadata, credits: result.credits };
      setMessages(updated);
      setFeedbackByIndex((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      await persist(updated);
    } catch (err) {
      setLoadError('send');
      setSendErrorMessage(assistantSendErrorMessage(err, t('sendFailed')));
    } finally {
      setSending(false);
    }
  };

  const handleFeedback = async (index: number, value: 'helpful' | 'notHelpful') => {
    const already = feedbackByIndex[index];
    const nextValue = already === value ? null : value;
    setFeedbackByIndex((prev) => ({ ...prev, [index]: nextValue }));
    const updated = [...messages];
    updated[index] = { ...updated[index], reaction: nextValue === 'helpful' ? 1 : nextValue === 'notHelpful' ? -1 : 0 };
    setMessages(updated);
    if (nextValue) {
      // Safe metadata only -- never the conversation body (spec requirement).
      submitFeedback({
        value: nextValue === 'helpful' ? 1 : -1,
        model: updated[index].model,
        lang: language,
        text: `screen=assistant;scope=${organization.state.toLowerCase()}`,
      }).catch(() => {});
    }
    if (activeConversationId) persist(updated).catch(() => {});
  };

  const handlePin = async () => {
    if (!activeConversation) return;
    await setConversationMeta(activeConversation.id, { pinned: !activeConversation.pinned });
    refreshConversations();
  };

  const handleArchive = async () => {
    if (!activeConversation) return;
    await setConversationMeta(activeConversation.id, { archived: !activeConversation.archived });
    refreshConversations();
  };

  const handleMoveToProject = async (projectId: string | null) => {
    if (!activeConversation) return;
    await setConversationMeta(activeConversation.id, { projectId });
    refreshConversations();
  };

  const handleDelete = async () => {
    if (!activeConversation) return;
    await deleteConversation(activeConversation.id);
    handleNewChat();
    refreshConversations();
  };

  const handleRename = async (title: string) => {
    if (!activeConversation) return;
    await setConversationMeta(activeConversation.id, { title });
    refreshConversations();
  };

  const handleCreateProject = async (name: string) => {
    const project = await createProject(name);
    setProjects((prev) => [project, ...prev]);
    setNewProjectOpen(false);
  };

  return (
    <div className={`ozira-cw-layout${mobileSidebarOpen ? ' is-mobile-sidebar-open' : ''}`}>
      <div className="ozira-assistant-mobile-topbar">
        <button type="button" className="ozira-assistant-mobile-menu" data-mobile-pane-trigger="Conversations" onClick={onMobileSidebarOpen} aria-label="Open conversations">
          <IconSidebar width={20} height={20} aria-hidden="true" />
        </button>
        <strong>Assistant</strong>
        <span className="ozira-assistant-mobile-scope">{organization.state === 'ORGANIZATION' ? 'Organization' : 'Personal'}</span>
      </div>
      <ConversationSidebar
        onConversationAction={async (conversation, action) => {
          if (action.type === 'share') { setSidebarShareId(conversation.id); return; }
          if (action.type === 'delete') {
            await deleteConversation(conversation.id);
            if (conversation.id === activeConversationId) handleNewChat();
          } else {
            const patch = action.type === 'pin' ? { pinned: !conversation.pinned }
              : action.type === 'archive' ? { archived: !conversation.archived }
              : action.type === 'rename' ? { title: action.title } : { projectId: action.projectId };
            await setConversationMeta(conversation.id, patch);
          }
          await refreshConversations();
        }}
        conversations={conversations}
        projects={projects}
        organizationTenantId={organization.state === 'ORGANIZATION' ? organization.tenantId : null}
        activeConversationId={activeConversationId}
        showArchived={showArchived}
        onSelect={handleSelect}
        onNewChat={handleNewChat}
        onSearch={setSearchQuery}
        onToggleArchived={() => setShowArchived((v) => !v)}
        onNewProject={() => {
          setNewProjectName('');
          setNewProjectOpen(true);
        }}
        onSelectProject={(id) => setActiveProjectId((prev) => (prev === id ? null : id))}
        activeProjectId={activeProjectId}
        onMobileClose={onMobileSidebarClose}
        onMobileCloseWithoutRestore={onMobileSidebarCloseWithoutRestore}
        mobileOpen={mobileSidebarOpen}
        onOpenSettings={onOpenSettings}
        activeSection={activeSection}
        onNavigate={onNavigate}
        collapsed={railCollapsed}
        onToggleCollapsed={handleToggleSidebarCollapsed}
        activeProductId="assistant"
        onNavigateProduct={onNavigateProduct}
      />

      <div
        className="ozira-cw-main"
        inert={mobileSidebarOpen ? '' : undefined}
        aria-hidden={mobileSidebarOpen ? true : undefined}
      >
        {activeSection !== 'assistant' ? (
          <div className="ozira-cw-section-screen">
            {activeSection === 'home' ? <HomeScreen /> : null}
            {activeSection === 'activity' ? <ActivityScreen /> : null}
            {activeSection === 'files' ? <FilesScreen /> : null}
          </div>
        ) : activeConversation ? (
          <ConversationHeader
            conversation={activeConversation}
            projects={projects}
            scopeLabel={organization.state === 'ORGANIZATION' ? t('organization') : t('personal')}
            onPin={handlePin}
            onArchive={handleArchive}
            onMoveToProject={handleMoveToProject}
            onDelete={handleDelete}
            onRename={handleRename}
            onShare={() => setConversationShareOpen(true)}
          />
        ) : (
          <header className="ozira-cw-header">
            <div className="ozira-cw-header-copy">
              <h2 className="ozira-cw-header-title">{t('assistantTitle')}</h2>
              <p className="ozira-cw-header-subtitle">Personal context · GPT route selected automatically</p>
            </div>
            <div className="ozira-cw-header-menu">
              <button type="button" className="ozira-cw-header-btn" aria-label="Assistant options" title="Assistant options" aria-expanded={assistantOptionsOpen} onClick={() => setAssistantOptionsOpen((open) => !open)}>···</button>
              {assistantOptionsOpen ? (
                <ul className="ozira-cw-menu" role="menu">
                  <li role="none"><button type="button" role="menuitem" onClick={() => { handleNewChat(); setAssistantOptionsOpen(false); }}>New chat</button></li>
                  <li role="none"><button type="button" role="menuitem" onClick={() => { onOpenSettings?.(); setAssistantOptionsOpen(false); }}>Settings</button></li>
                </ul>
              ) : null}
            </div>
          </header>
        )}

        <div className="ozira-cw-messages" role="log" aria-label={t('assistantTitle')}>
          {restoringConversation && <p role="status">Restoring conversation…</p>}
          {!restoringConversation && !loadError && messages.length === 0 && (
            <p className="ozira-cw-empty-hint">{t('emptyStateBody')}</p>
          )}
          {messages.map((message, index) => {
            const isLast = index === messages.length - 1;
            if (message.role === 'user') {
              return (
                <div className="ozira-cw-message-row ozira-cw-message-row--user" key={index}>
                  <div className="ozira-cw-bubble ozira-cw-bubble--user">{message.content}</div>
                </div>
              );
            }
            return (
              <div className="ozira-cw-message-row" key={index}>
                <div className="ozira-cw-assistant-block">
                  <div className="ozira-cw-bubble ozira-cw-bubble--assistant">
                    {renderAssistantContent(message.content, message.metadata, message.image ? { id: message.image, url: message.image } : undefined, setViewerImageUrl)}
                  </div>
                  {!(sending && isLast) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <OziraResponseActions
                        feedback={feedbackByIndex[index] ?? null}
                        onFeedback={(value) => handleFeedback(index, value)}
                        onCopy={() => navigator.clipboard?.writeText(message.content).catch(() => {})}
                        saved={savedIndexes.has(index)}
                        onSave={() => handleToggleSave(index)}
                        onRetry={() => handleRetryLast(index)}
                        onShare={activeConversationId ? () => setShareMessageIndex(index) : undefined}
                      />
                      <MessageMenu text={message.content} credits={message.credits} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {loadError && (
          <div className="ozira-cw-error" role="alert" aria-live="assertive">
            <span>{loadError === 'send' ? (sendErrorMessage || t('sendFailed')) : t('loadFailed')}</span>
            {loadError === 'send' && failedSend ? (
              <button type="button" className="ozira-cw-error-retry" onClick={handleRetrySend} disabled={sending}>
                {t('retry')}
              </button>
            ) : null}
          </div>
        )}
        {activeSection === 'assistant' ? (
          <Composer
            disabled={sending || restoringConversation || conversationLoadFailed}
            onSend={handleSend}
            onOpenTools={() => setToolsOpen(true)}
            onOpenConnectors={() => setConnectorsOpen(true)}
            onOpenLiveVoice={() => setLiveVoiceOpen(true)}
            modelId={modelId}
            effortId={effortId}
            onModelChange={(next) => {
              setModelId(next.modelId);
              setEffortId(next.effortId);
            }}
            attachedImageName={pendingImage?.file.name ?? null}
            onClearAttachedImage={() => setPendingImage(null)}
            imageToolActive={selectedTool === 'createImage'}
            imageSizeOption={imageSizeOption}
            onImageSizeChange={setImageSizeOption}
            onClearImageTool={() => setSelectedTool(null)}
            onOpenProject={() => {
              setNewProjectName('');
              setNewProjectOpen(true);
            }}
            prefillDraft={composerPrefill}
            startDictation={dictationTrigger}
          />
        ) : null}
      </div>

      {viewerImageUrl && (
        <ImageViewer
          url={viewerImageUrl}
          onClose={() => setViewerImageUrl(null)}
          onRequestEdit={(edit) => {
            setPendingImageEdit({ sourceUrl: edit.sourceUrl, operation: edit.operation, size: edit.size });
            setComposerPrefill({ text: edit.prompt, nonce: Date.now() });
          }}
          onRemoved={() => {}}
        />
      )}
      {toolsOpen && (
        <ToolsPicker
          onClose={() => setToolsOpen(false)}
          onSelectTool={(tool) => {
            // "Voice" has no separate backend concept -- it starts the same
            // real dictation the composer's dedicated mic button runs,
            // rather than setting inert state nothing downstream reads.
            if (tool === 'voice') { setDictationTrigger({ nonce: Date.now() }); return; }
            setSelectedTool(tool);
          }}
          onSelectFile={(file) => {
            const reader = new FileReader();
            reader.onload = () => {
              const [, base64 = ''] = String(reader.result).split(',');
              setPendingImage({ file, base64, mimeType: file.type });
            };
            reader.readAsDataURL(file);
          }}
          onOpenConnectors={() => {
            setToolsOpen(false);
            setConnectorsOpen(true);
          }}
        />
      )}
      {connectorsOpen && (
        <ConnectorChooser
          onClose={() => setConnectorsOpen(false)}
          onBack={() => { setConnectorsOpen(false); setToolsOpen(true); }}
        />
      )}
      {shareMessageIndex !== null && activeConversationId && (
        <ShareDialog
          resourceType="response"
          resourceId={activeConversationId}
          messageIndex={shareMessageIndex}
          onClose={() => setShareMessageIndex(null)}
        />
      )}
      {sidebarShareId && <ShareDialog resourceType="conversation" resourceId={sidebarShareId} onClose={() => setSidebarShareId(null)} />}
      {conversationShareOpen && activeConversationId && (
        <ShareDialog
          resourceType="conversation"
          resourceId={activeConversationId}
          onClose={() => setConversationShareOpen(false)}
        />
      )}
      {liveVoiceOpen && <LiveVoicePanel language={language} turns={messages.map((message) => ({ role: message.role, content: message.content }))} onClose={() => setLiveVoiceOpen(false)} onExchange={(user, assistant) => { const next = [...messages, { role: 'user' as const, content: user }, { role: 'assistant' as const, content: assistant, model: 'realtime' }]; setMessages(next); persist(next).catch(() => {}); }} />}

      {newProjectOpen && (
        <div className="ozira-cw-modal-backdrop" role="presentation" onClick={() => setNewProjectOpen(false)}>
          <div className="ozira-cw-modal" role="dialog" aria-modal="true" aria-labelledby="ozira-cw-new-project-title" onClick={(e) => e.stopPropagation()}>
            <h3 id="ozira-cw-new-project-title">{t('newProject')}</h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (newProjectName.trim()) handleCreateProject(newProjectName.trim());
              }}
            >
              <input
                className="ozira-cw-rename-input"
                autoFocus
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                aria-label={t('newProject')}
                style={{ width: '100%' }}
              />
              <div className="ozira-cw-modal-actions">
                <button type="button" className="ozira-button ozira-button--secondary" onClick={() => setNewProjectOpen(false)}>
                  {t('cancel')}
                </button>
                <button type="submit" className="ozira-button" disabled={!newProjectName.trim()}>
                  {t('newProject')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
